// =====================================================
// COMPONENTE: VGVGeralVendas
// =====================================================

import { useState, useEffect } from 'react'
import { Eye, TrendingUp, TrendingDown } from 'lucide-react'
import { formatCurrency } from '../utils/formatCurrency'
import { supabase } from '../services/supabase'
import { mesAtualKey, parseMesFiltro, type MesFiltro } from '../utils/filtroMes'

interface VGVGeralVendasProps {
    className?: string
    mesFiltro?: MesFiltro
}

export function VGVGeralVendas({ className = '', mesFiltro = mesAtualKey() }: VGVGeralVendasProps) {
    const [totalVGV, setTotalVGV] = useState<number>(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string>('')
    const [variacao, setVariacao] = useState<number>(0)
    const [quantidadeImoveis, setQuantidadeImoveis] = useState<number>(0)

    useEffect(() => {
        loadVGV()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mesFiltro])

    const loadVGV = async () => {
        try {
            setLoading(true)
            setError('')

            const { data: { user }, error: userError } = await supabase.auth.getUser()

            if (userError || !user) {
                throw new Error('Usuário não autenticado')
            }

            const { data: corretor, error: corretorError } = await supabase
                .from('corretores')
                .select('id')
                .eq('user_id', user.id)
                .single()

            if (corretorError) {
                throw new Error('Corretor não encontrado')
            }

            const { inicio, fim, inicioAnterior, fimAnterior } = parseMesFiltro(mesFiltro)

            const { data: vendasAtual, error: vendasAtualError } = await supabase
                .from('vendas')
                .select('valor_venda, created_at')
                .eq('corretor_id', corretor.id)
                .gte('created_at', inicio.toISOString())
                .lte('created_at', fim.toISOString())
                .order('created_at', { ascending: false })

            const { data: vendasAnterior, error: vendasAnteriorError } = await supabase
                .from('vendas')
                .select('valor_venda, created_at')
                .eq('corretor_id', corretor.id)
                .gte('created_at', inicioAnterior.toISOString())
                .lte('created_at', fimAnterior.toISOString())

            if (vendasAtualError || vendasAnteriorError) {
                throw vendasAtualError || vendasAnteriorError
            }

            const vgvAtual = vendasAtual?.reduce((total, venda) => {
                return total + (venda.valor_venda || 0)
            }, 0) || 0

            const vgvAnterior = vendasAnterior?.reduce((total, venda) => {
                return total + (venda.valor_venda || 0)
            }, 0) || 0

            let variacaoCalculada = 0
            if (vgvAnterior > 0) {
                variacaoCalculada = ((vgvAtual - vgvAnterior) / vgvAnterior) * 100
            } else if (vgvAtual > 0) {
                variacaoCalculada = 100
            }

            setTotalVGV(vgvAtual)
            setQuantidadeImoveis(vendasAtual?.length || 0)
            setVariacao(variacaoCalculada)
        } catch (err) {
            console.error('❌ Erro ao carregar VGV:', err)
            setError('Erro ao carregar VGV')
        } finally {
            setLoading(false)
        }
    }

    const formatVariacao = (valor: number) => {
        if (valor === 0) return '0.0%'
        const sinal = valor >= 0 ? '+' : ''
        const valorAbsoluto = Math.abs(valor)
        if (valorAbsoluto >= 100) {
            return `${sinal}${valor.toFixed(0)}%`
        }
        return `${sinal}${valor.toFixed(1)}%`
    }

    const getVariacaoColor = (valor: number) => {
        return valor >= 0 ? 'text-green-600' : 'text-red-600'
    }

    const getVariacaoIcon = (valor: number) => {
        return valor >= 0 ? TrendingUp : TrendingDown
    }

    const VariacaoIcon = getVariacaoIcon(variacao)

    if (loading) {
        return (
            <div className={`bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
                <div className="mb-3 text-right">
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 tracking-wide">Imóveis</p>
                </div>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className={`bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
                <div className="mb-3 text-right">
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 tracking-wide">Imóveis</p>
                </div>
                <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                        <button
                            onClick={loadVGV}
                            className="mt-2 text-blue-600 dark:text-blue-400 text-sm hover:underline"
                        >
                            Tentar novamente
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={`bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
            <div className="mb-3 text-right">
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 tracking-wide">Imóveis</p>
            </div>
            <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className={`flex items-center ${getVariacaoColor(variacao)}`}>
                    <VariacaoIcon className="w-4 h-4 mr-1" />
                    <span className="text-sm font-medium">{formatVariacao(variacao)}</span>
                </div>
            </div>
            <div>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(totalVGV)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">VGV</p>
                <p className="text-sm font-semibold text-green-600 dark:text-green-400 mt-1">
                    {quantidadeImoveis} imóveis vendidos
                </p>
            </div>
        </div>
    )
}
